// =============================================================================
// MeuDriver — M4: Hook de Mensagens com suporte a leitura e contagem de não lidas
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { RealtimeChannel } from '@supabase/supabase-js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UnreadCount {
  booking_id: string
  unread_count: number
}

// ---------------------------------------------------------------------------
// useUnreadCounts — mapa de {booking_id: count} para o usuário autenticado
// ---------------------------------------------------------------------------

export function useUnreadCounts() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const channelRef = useRef<RealtimeChannel | null>(null)

  // Subscription Realtime: ao receber mensagem nova, invalida unreadCounts
  useEffect(() => {
    if (!user?.id) return

    const channel = supabase
      .channel(`unread-counts-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
        },
        (payload) => {
          // Só invalida se a mensagem foi enviada por outro usuário
          const newMsg = payload.new as { sender_id: string }
          if (newMsg.sender_id !== user.id) {
            queryClient.invalidateQueries({ queryKey: ['unread-counts', user.id] })
          }
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['unread-counts', user.id] })
        },
      )
      .subscribe()

    channelRef.current = channel

    return () => {
      channel.unsubscribe()
    }
  }, [user?.id, queryClient])

  return useQuery<Record<string, number>>({
    queryKey: ['unread-counts', user?.id],
    enabled: !!user?.id,
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60, // Polling de fallback a cada 1 min
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_unread_message_counts')
      if (error) throw error

      const rows = (data ?? []) as UnreadCount[]
      return Object.fromEntries(rows.map((r) => [r.booking_id, Number(r.unread_count)]))
    },
  })
}

// ---------------------------------------------------------------------------
// useTotalUnread — soma de todas as contagens
// ---------------------------------------------------------------------------

export function useTotalUnread(): number {
  const { data } = useUnreadCounts()
  if (!data) return 0
  return Object.values(data).reduce((s, c) => s + c, 0)
}

// ---------------------------------------------------------------------------
// useMarkAsRead — chama RPC ao abrir conversa
// ---------------------------------------------------------------------------

export function useMarkAsRead(bookingId: string | null) {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (!bookingId) return 0
      const { data, error } = await supabase.rpc('mark_messages_read', {
        p_booking_id: bookingId,
      })
      if (error) throw error
      return data as number
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ['unread-counts', user.id] })
        queryClient.invalidateQueries({ queryKey: ['conversations', user.id] })
      }
    },
  })
}
